<template>
	<Transition @enter="showSmartBanner" @leave="hideSmartBanner">
		<SmartBanner v-show="!state.visibleMobileMenu" />
	</Transition>
	<div class="lg:min-h-[6.875rem] max-lg:sticky max-lg:top-0 max-lg:z-sticky max-lg:inset-x-0">
		<header
			id="header"
			ref="headerRef"
			class="bg-background-primary shadow-green-sm lg:fixed lg:inset-x-0 lg:top-0 lg:z-sticky"
			aria-label="Hlavička"
		>
			<div class="container">
				<div class="flex items-center gap-4 xxl:gap-6">
					<div class="flex-none lg:hidden items-center flex after:border-r after:border-background-secondary after:absolute after:left-full after:h-9 relative pr-4 max-xs:pl-2">
						<button
							type="button"
							class="rounded-full h-[3.75rem] pr-4 relative focus:outline-hidden"
							aria-controls="mobile-primary-menu"
							:aria-expanded="state.visibleMobileMenu"
							aria-label="Zobrazení hlavní navigace"
							@click="toggleMobileMenu"
						>
							<span class="block w-4 absolute-center">
								<span
									aria-hidden="true"
									class="block absolute h-0.5 w-4 bg-icon-primary rounded-xs transform transition duration-500 ease-in-out"
									:class="{'rotate-45': state.visibleMobileMenu,' -translate-y-1.5': !state.visibleMobileMenu}"
								/>
								<span
									aria-hidden="true"
									class="block absolute h-0.5 w-4 bg-icon-primary rounded-xs transform transition duration-500 ease-in-out"
									:class="{'opacity-0': state.visibleMobileMenu} "
								/>
								<span
									aria-hidden="true"
									class="block absolute h-0.5 w-3 bg-icon-primary rounded-xs transform transition duration-500 ease-in-out"
									:class="{'-rotate-45 w-4': state.visibleMobileMenu, ' translate-y-1.5': !state.visibleMobileMenu}"
								/>
							</span>
						</button>
					</div>
					<div class="flex-none py-2 flex items-center gap-1 xl:gap-2">
						<AbsoluteInternalLink
							to="/"
							aria-label="Přejít na hlavní stránku"
							@click="handleLogoClick()"
						>
							<NuxtIcon
								name="custom/globus"
								filled
								class="brand [&_svg]:transition-all [&_svg]:duration-200 [&_svg]:max-lg:!w-[3rem] [&_svg]:max-lg:!h-[1.9375rem]"
								:class="{'[&_svg]:lg:!w-[3rem] [&_svg]:lg:!h-[1.9375rem]': isSticky}"
							/>
						</AbsoluteInternalLink>
						<HeaderCampaignLogo :is-sticky="isSticky" />
					</div>

					<BreakpointRender :breakpoint="isDesktop" class="flex-none lg:flex-auto max-lg:ml-auto transition-all" :class="{'xxl:pl-8': !isSticky}">
						<template #true>
							<Transition
								@enter="onEnterSecondaryMenu"
								@leave="onLeaveSecondaryMenu"
							>
								<div v-if="!isSticky">
									<div class="flex gap-x-2 xxl:gap-x-4 overflow-hidden items-center relative pt-2">
										<SearchMain :is-sticky-header="isSticky" />
										<HeaderNavList :menu-links="menuItemsSecondary" class="ml-auto" />
										<HeaderUserBar v-model:model="state" class="shrink-0 max-lg:ml-auto" />
									</div>
								</div>
							</Transition>
							<div class="flex justify-between" :class="isSticky ? 'py-2' : 'pt-2'">
								<div class="relative grow mr-4 xxl:mr-6">
									<HeaderNavList :menu-links="menuItems" :font-bold="isSticky ? false : true">
										<template #prefix>
											<HeaderNavButton :button="menuItemHypermarket" font-bold />
											<AbsoluteInternalLink
												v-if="selectedHypermarket"
												:to="`/${selectedHypermarket}`"
												class="flex items-center py-3 px-1.5 xl:px-2 relative transition ease after:transition after:ease after:absolute after:bottom-0 after:inset-x-0 after:h-[.1875rem] after:rounded-t-lg hover:after:bg-background-important"
												aria-label=""
											>
												<NuxtIcon
													name="custom/home"
													class="text-icon-primary text-lg"
													filled
													aria-hidden="true"
												/>
											</AbsoluteInternalLink>
										</template>
									</HeaderNavList>
									<Transition name="fade">
										<SearchMain
											v-if="isSticky && state.visibleCollapsingSearch"
											:is-sticky-header="isSticky"
											show-button
											focus-input-border
											class="absolute inset-0 !max-w-full"
										/>
									</Transition>
								</div>
								<Transition name="fade">
									<LazyHeaderUserBarLite v-if="isSticky" v-model:model="state" class="ml-auto flex" />
								</Transition>
							</div>
						</template>
						<template #false>
							<LazyHeaderUserBarLite v-model:model="state" />
						</template>
					</BreakpointRender>
				</div>
				<BreakpointRender :breakpoint="isDesktop">
					<template #false>
						<div v-if="state.visibleCollapsingSearch" class="pb-2">
							<SearchMain
								:is-sticky-header="isSticky"
								show-button
								focus-input-border
								class="grow !max-w-full"
							/>
						</div>
					</template>
				</BreakpointRender>
			</div>
		</header>
	</div>
	<ClientTeleport>
		<HeaderNavAside v-model:model="state" :menu-links="menuItems" :menu-secondary-links="menuItemsSecondary" />
	</ClientTeleport>
</template>

<script setup lang="ts">
	import {gsap} from 'gsap';
	import {storeToRefs} from 'pinia';
	import type {Header} from '~/types/header';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import useTailwindBreakpoints from '~/composables/useTailwindBreakpoints';

	const headerRef = useTemplateRef<HTMLElement | null>('headerRef');

	const breakpoints = useTailwindBreakpoints();
	const isDesktop = breakpoints.greaterOrEqual('lg');

	const {isSticky} = useStickyHeader(headerRef, isDesktop);

	const {
		menuItems,
		menuItemsSecondary,
		menuItemHypermarket,
	} = useHeaderMenu(isDesktop);

	const state = reactive<Header>({
		visibleMobileMenu: false,
		visibleCollapsingSearch: false,
		visibleUserMenu: false,
	});

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);

	if (import.meta.client) {
		const bus = useEventBus<void>('navAside:close');
		bus.on(closeMenu);
	}

	const {width} = useWindowSize();

	const bodyScrollLock = useBodyScrollLock();
	function toggleMobileMenu() {
		state.visibleMobileMenu = !state.visibleMobileMenu;
		state.visibleCollapsingSearch = false;
		state.visibleMobileMenu ? trackHeaderEvent('hamburger_icon_top') : trackHeaderEvent('close_icon_top');
	}

	function closeMenu() {
		state.visibleMobileMenu = false;
	}

	function showSmartBanner(el: Element, onComplete: () => void) {
		gsap.to(el, {opacity: 1, marginTop: 0, onComplete});
	}

	function hideSmartBanner(el: Element, onComplete: () => void) {
		const height = el.clientHeight;
		gsap.to(el, {opacity: 0, marginTop: -height, onComplete});
	}

	function onEnterSecondaryMenu(el: Element, done: () => void) {
		gsap.fromTo(el, {height: 0, y: -el.clientHeight}, {height: el.clientHeight, y: 0, duration: 0.2, clearProps: 'all', onComplete: done});
	}

	function onLeaveSecondaryMenu(el: Element, done: () => void) {
		gsap.to(el, {height: 0, y: -el.clientHeight, duration: 0.2, onComplete: done});
	}

	function handleLogoClick() {
		trackHeaderEvent('globus_logo');

		if (isSticky.value || !isDesktop.value) {
			gsap.to(window, {scrollTo: 0});
		}
	}

	watch(isSticky, (newValue) => {
		if (!newValue) {
			state.visibleCollapsingSearch = false;
		}
	});

	watch(() => state.visibleMobileMenu, (newValue) => {
		bodyScrollLock.value = newValue;
	}, {immediate: true});

	watchDebounced(
		width,
		() => { closeMenu(); },
		{debounce: 250, maxWait: 1000},
	);
</script>
